<template>
  <div
    :class="{
      invalid: showError && !disabled,
      valid: isValid && !disabled,
      focused,
      active,
      disabled: disabled || form.loading,
      label,
    }"
    class="sf-select">
    <div class="sf-select__container">
      <div class="sf-select__label">{{ label }}</div>
      <v-select
        ref="element"
        v-model="val"
        :options="parsedOptions"
        :searchable="searchable"
        :show-labels="false"
        :allow-empty="allowEmpty"
        :loading="loading || asyncLoading"
        :internal-search="typeof search !== 'function'"
        :show-no-options="false"
        :show-no-results="!asyncLoading"
        v-bind="$attrs"
        label="label"
        track-by="value"
        open-direction="below"
        class="sf-select__element"
        @search-change="enqueue"
        @open="onFocus"
        @close="onBlur() + isValue()">
        <template v-if="option.img" slot="singleLabel" slot-scope="{ option }">
          <img class="multiselect__img" :src="option.img">
          {{ option.label }}
        </template>
        <template v-if="clearable">
          <span v-if="model" slot="caret" class="sf-select__clear" @mousedown.stop="clear">✕</span>
        </template>
        <template slot="placeholder">
          <slot name="placeholder"/>
        </template>
        <span slot="noOptions">&nbsp;</span>
        <slot slot="noResult" name="noResult">&nbsp;</slot>
        <template slot="option" slot-scope="{ option }">
          <img v-if="option.img" class="multiselect__img" :src="option.img">
          <span v-qa="`select-value-${option.value}`">
            {{ option.label }}
          </span>
        </template>
      </v-select>
    </div>
    <transition name="v-fade">
      <div v-show="showTooltip" class="sf-tooltip" v-text="tooltip"/>
    </transition>
    <div
      v-qa="errorElement.selector"
      :class="{ active: showError }"
      class="sf-error"
      v-text="errorElement.message"/>
  </div>
</template>

<script>
import VSelect from 'vue-multiselect';
import Field from './field.vue';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  components: { VSelect },
  extends: Field,
  props: {
    value: {
      type: null,
      default: '',
    },
    options: {
      type: Array,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    search: {
      type: Function,
      default: null,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    allowCustom: {
      type: Boolean,
      default: false,
    },
    parent: {
      type: String,
      default: '',
    },
    delay: {
      type: Number,
      default: 300,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    opts: [],
    asyncLoading: false,
    parentEl: null,
    queue: null,
    savedVal: {},
  }),
  computed: {
    parsedOptions() {
      const { options = this.opts } = this;

      return options.map(option => {
        if (typeof option !== 'object') {
          return { label: option, value: option };
        }

        return option;
      });
    },
    val: {
      get() {
        const item = this.parsedOptions.find(({ value }) => value === this.model);
        return item;
      },
      set(item) {
        if (item && typeof item === 'object') this.savedVal = item;
        const value = item && typeof item === 'object' ? item.value : item;
        this.setValue(value);
      },
    },
  },
  watch: {
    parent: {
      immediate: true,
      handler(newValue, oldValue) {
        // Detach old parent input listener
        if (oldValue) this.parentEl.$off('input', this.onParentInput);

        // Attach new parent input listener
        if (newValue) {
          this.parentEl = this.$parent.$children.find(({ name }) => name === this.parent) || null;
          if (this.parentEl) this.parentEl.$on('input', this.onParentInput);
        }
      },
    },
  },
  methods: {
    enqueue(input) {
      clearTimeout(this.queue);
      this.queue = setTimeout(async () => {
        await this.searchChange(input);
      }, this.delay);
    },

    clear() {
      this.setValue('');
    },

    async searchChange(input) {
      this.$emit('search-change', input);
      if (this.search && (this.focused || this.parent)) {
        this.asyncLoading = true;
        const response = await this.search(input, this.parentEl && this.parentEl.model);
        if (response) {
          let result;

          if (Array.isArray(response)) {
            result = response;
          } else if (response.data && Array.isArray(response.data)) {
            result = response.data;
          } else {
            result = [];
          }

          if (this.allowCustom) {
            result.unshift({ value: input, label: input });
          }

          this.opts = result;
        }
        this.asyncLoading = false;
      }
    },

    onParentInput() {
      this.opts = [];
      this.val = '';
      this.enqueue('');
    },
    // On blur event check if value is valid
    // If not, set previous valid value
    isValue() {
      // Temporary fix, while creating new component for autocomplete
      if (!this.val && this.opts.length > 0) {
        this.val = this.opts[0];
      }
      // if (!this.val && this.savedVal && this.model) {
      //   this.opts = [this.savedVal];
      // }
    },
  },
};
</script>
